import { useCallback, useEffect, useState } from 'react';

export const AGE_AFFIRMED_KEY = 'wx_age_affirmed';

export type AgeAffirmationStatus = 'loading' | 'affirmed' | 'unaffirmed';

/**
 * Whether this browser has affirmed legal drinking age.
 *
 * Only the outcome is persisted — a boolean and the date it was affirmed. We
 * never store the date of birth of someone who has not registered, and we
 * store nothing at all for someone we refused: a refused visitor who comes
 * back gets a fresh gate rather than a rejected date sitting in an editable
 * field.
 *
 * This is a client-side affirmation and is bypassable by clearing site data or
 * opening a private window. That is the accepted industry norm for an
 * unauthenticated gate; it is not a substitute for the server-side check in
 * `auth.register`, which is what actually binds an age claim to an identity.
 */
export const useAgeAffirmation = () => {
  // Read synchronously on first render. localStorage is not async, and going
  // through an effect would render one frame of the gate to an already
  // affirmed visitor on every single page load.
  const [status, setStatus] = useState<AgeAffirmationStatus>(() => {
    try {
      const raw = localStorage.getItem(AGE_AFFIRMED_KEY);
      return raw && JSON.parse(raw)?.affirmed ? 'affirmed' : 'unaffirmed';
    } catch {
      // A browser that will not answer — private mode, blocked site data,
      // corrupt JSON — is treated as never having affirmed. Failing closed
      // shows the gate again; failing open would show alcohol content to
      // someone unverified.
      return 'unaffirmed';
    }
  });

  const affirm = useCallback(() => {
    try {
      localStorage.setItem(
        AGE_AFFIRMED_KEY,
        JSON.stringify({ affirmed: true, affirmedAt: new Date().toISOString() })
      );
    } catch {
      // Storage failed, but the person in front of us has affirmed. Let them
      // through for this session; the gate returns on the next visit.
    }
    setStatus('affirmed');
  }, []);

  // Another tab affirming (or clearing site data) should not leave this tab
  // out of step — the `storage` event only fires in the tabs that did not
  // make the change, which is exactly the ones that need telling.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== AGE_AFFIRMED_KEY) return;
      try {
        const raw = localStorage.getItem(AGE_AFFIRMED_KEY);
        setStatus(raw && JSON.parse(raw)?.affirmed ? 'affirmed' : 'unaffirmed');
      } catch {
        setStatus('unaffirmed');
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return { status, affirm };
};
